import { useCallback, useEffect } from 'react';
import { Cart } from '@shared/types/cart/Cart';
import { sdk } from '@/sdk';
import useSWR from 'swr';
import { Address } from '@shared/types/account/Address';
import { Order } from '@shared/types/cart';
import { calculateTransaction } from '@/lib/utils/calculate-transaction';
import { CheckoutPayload } from './types';
import Service from '@/service';
import getCookieValue from '@/utils/cookie';
import { getCurrencyByLocale } from '@/utils/currency-helpers';
import { usePathname } from 'next/navigation';
import { useCartContext } from '@/providers/cart/Context';
import useAccountRoles from '../useAccountRoles';

const useCart = (businessUnitKey?: string, storeKey?: string) => {
  const locale = getCookieValue('locale');
  const currencyCode = getCurrencyByLocale(locale);
  const pathname = usePathname();
  const { isCSUser } = useAccountRoles();
  const { cartDetails, setCartDetails } = useCartContext();

  const getCart = useCallback(
    async () => {
      const result = await Service.cart.getCart({ businessUnitKey: businessUnitKey as string, currency: currencyCode, storeKey: storeKey as string });
      setCartDetails(result?.data);
      return result;
    },
    [businessUnitKey, storeKey, currencyCode],
  );

  const addToCart = useCallback(
    async (variantSku: string, variantKey: string, quantity?: number) => {
      const result = await Service.cart.addToCart({ currency: currencyCode, businessUnitKey: businessUnitKey as string, variantSku, variantKey, quantity, storeKey: storeKey as string });
      if (result?.data) {
        setCartDetails(result.data);
      }
      return result;
    },
    [businessUnitKey, storeKey, currencyCode],
  );

  const updateItemQuantity = useCallback(
    async (variantSku: string, quantity: number, variantKey: any) => {
      const result = await Service.cart.updateItemQuantity({ businessUnitKey: businessUnitKey as string, currency: currencyCode, variantSku, quantity, variantKey, storeKey: storeKey as string });
      if (result?.data) {
        setCartDetails(result.data);
      }
      return result;
    },
    [businessUnitKey, storeKey, currencyCode],
  );

  const removeItemFromCart = useCallback(
    async (variantSku: string) => {
      const result = await Service.cart.removeItem({ businessUnitKey: businessUnitKey as string, currency: currencyCode, variantSku, storeKey: storeKey as string });
      if (result?.data) {
        setCartDetails(result.data);
      }
      return result;
    },
    [businessUnitKey, storeKey, currencyCode],
  );

  const removeAllItemsFromCart = useCallback(
    async () => {
      const result = await Service.cart.removeAllItems({ businessUnitKey: businessUnitKey as string, currency: currencyCode, storeKey: storeKey as string });
      if (result?.data) {
        setCartDetails(result.data);
      }
      return result;
    },
    [businessUnitKey, storeKey, currencyCode],
  );

  const removeCoupon = useCallback(
    async (discountCode: string) => {
      const result = await Service.cart.removeCoupon({ currency: currencyCode, discountCode, storeKey: storeKey as string });
      if (result?.data && !result?.data?.errorMessage) {
        setCartDetails(result.data);
      }
      return result;
    },
    [storeKey, currencyCode],
  );

  const addCoupon = useCallback(
    async (discountCode: string) => {
      const result = await Service.cart.addCoupon({ currency: currencyCode, discountCode, storeKey: storeKey as string });
      if (result?.data && !result?.data?.errorMessage) {
        setCartDetails(result.data);
      }
      return result;
    },
    [storeKey, currencyCode],
  );

  const submitOrder = useCallback(
    async () => {
      const result = await Service.cart.submitOrder({ companyKey: businessUnitKey as string, currency: currencyCode });
      if (result?.data) {
        setCartDetails(result.data);
      }
      return result;
    },
    [businessUnitKey, storeKey, currencyCode],
  );

  const addItem = useCallback(
    async (lineItems: Array<{ sku: string; count: number }>) => {
      const payload = { lineItems: lineItems.map(({ sku, count }) => ({ variant: { sku }, count })), businessUnitKey };

      const result = await sdk.composableCommerce.cart.addItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      return result.isError ? {} : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const updateItem = useCallback(
    async (lineItem: { id: string; count: number }) => {
      const payload = { lineItem, businessUnitKey };

      const result = await sdk.composableCommerce.cart.updateItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      return result.isError ? {} : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const removeItem = useCallback(
    async (lineItemId: string) => {
      const payload = { lineItem: { id: lineItemId }, businessUnitKey };

      const result = await sdk.composableCommerce.cart.removeItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      return result.isError ? {} : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const requestQuote = useCallback(
    async (payload: { buyerComment: string }) => {
      const result = await sdk.composableCommerce.quote.createQuote(
        { comment: payload.buyerComment },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );



      return result.isError ? {} : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const updateCart = useCallback(
    async ({ shipping, billing, email }: { shipping?: Address; billing?: Address; email?: string }) => {
      const result = await sdk.composableCommerce.cart.updateCart(
        { shipping, billing, ...(email ? { account: { email } } : {}) },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );



      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const setShippingMethod = useCallback(
    async (shippingMethodId: string) => {
      const result = await sdk.composableCommerce.cart.setShippingMethod(
        { shippingMethod: { id: shippingMethodId } },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );



      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const redeemDiscount = useCallback(
    async (code: string) => {
      const result = await sdk.composableCommerce.cart.redeemDiscountCode(
        { code },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );



      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const removeDiscount = useCallback(
    async (discountId: string) => {
      const result = await sdk.composableCommerce.cart.removeDiscountCode(
        { discountId },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );



      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const checkout = useCallback(
    async ({ purchaseOrderNumber }: CheckoutPayload) => {
      const result = await sdk.composableCommerce.cart.checkout(
        { purchaseOrderNumber },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );


      return result.isError ? ({} as Partial<Order>) : result.data;
    },
    [businessUnitKey, storeKey],
  );

  const getShippingMethods = useCallback(async () => {
    const result = await sdk.composableCommerce.cart.getShippingMethods({
      businessUnitKey,
      storeKey,
      onlyMatching: true,
    });

    return result.isError ? [] : result.data;
  }, [businessUnitKey, storeKey]);

  const { data: shippingMethods } = useSWR(
  (pathname?.includes('/cart') || pathname?.includes('/checkout')) || pathname?.includes('/review') && cartDetails 
    ? ['/action/cart/getShippingMethods', businessUnitKey, storeKey] 
    : null,
  getShippingMethods,
  {
    revalidateOnFocus: false,  // Prevent refetching when window regains focus
    revalidateOnReconnect: false  // Prevent refetching on reconnection
  }
);


  return {
    cart: cartDetails ? { ...cartDetails, transaction: calculateTransaction(cartDetails) } : undefined,
    totalItems: cartDetails?.lineItems?.reduce((acc, curr) => acc + (curr.count ?? 1), 0) ?? 0,
    addItem,
    updateItem,
    removeItem,
    requestQuote,
    updateCart,
    setShippingMethod,
    redeemDiscount,
    removeDiscount,
    checkout,
    shippingMethods,
    addToCart,
    updateItemQuantity,
    removeItemFromCart,
    removeAllItemsFromCart,
    submitOrder,
    getCart,
    addCoupon,
    removeCoupon,
  };
};

export default useCart;
